import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { StatusPill, PriorityPill, TypePill } from "@/components/StatusPill";
import { formatDate, formatDateTime, formatDuration, relTime, TYPE_LABEL, currency, ROLE_LABEL } from "@/lib/format";
import { ArrowLeft, MessageSquare, Send, Play, CheckCircle2, RotateCcw, Trash2, Loader2, ImageIcon, IndianRupee, Building2, Camera, ArrowRightLeft, UserCheck, HardHat, Phone, Pencil, Package, Repeat } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AuthImage from "@/components/AuthImage";
import PhotoUploader from "@/components/PhotoUploader";
import InstallationChecklist from "@/components/InstallationChecklist";

export default function TicketDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [outlet, setOutlet] = useState(null);
  const [comment, setComment] = useState("");
  const [posting, setPosting] = useState(false);

  const [afterPhotos, setAfterPhotos] = useState([]);
  const [resNote, setResNote] = useState("");
  const [resolving, setResolving] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(false);

  const [payOpen, setPayOpen] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payNote, setPayNote] = useState("");
  const [payReceipt, setPayReceipt] = useState([]);
  const [paying, setPaying] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);

  const [fcs, setFcs] = useState([]);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [newFcId, setNewFcId] = useState("");
  const [reassigning, setReassigning] = useState(false);

  const [vendors, setVendors] = useState([]);
  const [assets, setAssets] = useState([]);
  const [vendorDialogOpen, setVendorDialogOpen] = useState(false);
  const [newVendorId, setNewVendorId] = useState("");
  const [savingVendor, setSavingVendor] = useState(false);
  const [assetDialogOpen, setAssetDialogOpen] = useState(false);
  const [newAssetId, setNewAssetId] = useState("");
  const [savingAsset, setSavingAsset] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/tickets/${id}`);
      setTicket(data);
      const outs = await api.get("/outlets");
      setOutlet(outs.data.find((o) => o.id === data.outlet_id));
      try {
        const { data: list } = await api.get("/users/facility-incharges");
        setFcs(list);
      } catch { /* outlet_incharge cannot list — silently skip */ }
      try {
        const [vRes, aRes] = await Promise.all([api.get("/vendors"), api.get("/assets")]);
        setVendors(vRes.data);
        setAssets(aRes.data);
      } catch { /* silent */ }
    } catch (err) {
      toast.error("Could not load ticket");
      nav("/tickets");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!ticket) return <div className="p-8 text-slate-500">Loading…</div>;

  const isManager = user?.role === "manager" || user?.role === "op_manager";
  const canWork = user?.role === "admin" || user?.role === "facility_incharge";

  const postComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setPosting(true);
    try {
      await api.post(`/tickets/${id}/comments`, { text: comment.trim() });
      setComment("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not post");
    } finally { setPosting(false); }
  };

  const startWork = async () => {
    try { await api.post(`/tickets/${id}/start`); toast.success("Work started"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const resolveNow = async () => {
    if (ticket.type !== "internal_job" && afterPhotos.length === 0) {
      toast.error("Add at least one after-photo to resolve");
      return;
    }
    setResolving(true);
    try {
      await api.post(`/tickets/${id}/resolve`, { after_photo_ids: afterPhotos, resolution_note: resNote });
      toast.success("Ticket resolved");
      setResolveOpen(false);
      setAfterPhotos([]); setResNote("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setResolving(false); }
  };

  const reopen = async () => {
    try { await api.post(`/tickets/${id}/reopen`); toast.success("Ticket reopened"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Cannot reopen"); }
  };

  const deleteTicket = async () => {
    if (!confirm("Delete this ticket permanently?")) return;
    try { await api.delete(`/tickets/${id}`); toast.success("Deleted"); nav("/tickets"); }
    catch (err) { toast.error("Failed to delete"); }
  };

  const submitPayment = async (e) => {
    e.preventDefault();
    const amt = parseFloat(payAmount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    setPaying(true);
    try {
      if (editingPayment) {
        await api.put(`/tickets/${id}/payments/${editingPayment.id}`, {
          amount: amt, note: payNote, receipt_id: payReceipt[0] || null,
        });
        toast.success("Payment updated");
      } else {
        await api.post(`/tickets/${id}/payments`, {
          amount: amt, note: payNote, receipt_id: payReceipt[0] || null,
        });
        toast.success("Payment logged");
      }
      setPayOpen(false); setEditingPayment(null);
      setPayAmount(""); setPayNote(""); setPayReceipt([]);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setPaying(false); }
  };

  const openEditPayment = (p) => {
    setEditingPayment(p);
    setPayAmount(String(p.amount));
    setPayNote(p.note || "");
    setPayReceipt(p.receipt_id ? [p.receipt_id] : []);
    setPayOpen(true);
  };

  const removePayment = async (p) => {
    if (!confirm(`Delete payment of ₹${p.amount}? This updates the ticket total.`)) return;
    try {
      await api.delete(`/tickets/${id}/payments/${p.id}`);
      toast.success("Payment deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
  };

  const reassign = async () => {
    if (!newFcId) return toast.error("Pick a Facility Incharge");
    setReassigning(true);
    try {
      await api.post(`/tickets/${id}/assign`, { assigned_fc_id: newFcId });
      toast.success("Ticket transferred");
      setReassignOpen(false); setNewFcId("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not transfer ticket");
    } finally { setReassigning(false); }
  };

  const canReassign = ticket.status !== "resolved" && (
    user?.role === "admin" ||
    user?.role === "manager" ||
    (user?.role === "facility_incharge" && ticket.assigned_fc_id === user.id)
  ) && fcs.length > 0;

  const canEditMeta = ticket.status !== "resolved" && (
    user?.role === "admin" ||
    user?.role === "manager" ||
    (user?.role === "facility_incharge" && ticket.assigned_fc_id === user.id)
  );

  const outletAssets = assets.filter(a => a.outlet_id === ticket.outlet_id);

  const saveVendor = async () => {
    setSavingVendor(true);
    try {
      const body = { vendor_id: newVendorId === "__none__" ? null : newVendorId };
      await api.post(`/tickets/${id}/assign-vendor`, body);
      toast.success("Vendor updated");
      setVendorDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update vendor");
    } finally { setSavingVendor(false); }
  };

  const saveAsset = async () => {
    setSavingAsset(true);
    try {
      const body = { asset_id: newAssetId === "__none__" ? null : newAssetId };
      await api.post(`/tickets/${id}/assign-asset`, body);
      toast.success("Asset updated");
      setAssetDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update asset");
    } finally { setSavingAsset(false); }
  };

  const isInternalJob = ticket.type === "internal_job";
  const isNewInstallation = ticket.type === "new_installation";
  const checklist = ticket.checklist || [];
  const checklistAllDone = isNewInstallation && checklist.length > 0 && checklist.every((x) => x.done);
  const canEditChecklist = (
    user?.role === "admin" ||
    (user?.role === "facility_incharge" && ticket.assigned_fc_id === user.id)
  ) && ticket.status !== "resolved";

  return (
    <div className="max-w-4xl mx-auto space-y-5 pb-24">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      {/* Header card */}
      <div className={`rounded-2xl border p-5 sm:p-6 ${ticket.is_overdue ? "overdue-tint" : "bg-white border-slate-200"}`}>
        <div className="flex flex-wrap items-center gap-1.5 mb-3">
          <TypePill type={ticket.type} />
          <PriorityPill priority={ticket.priority} />
          <StatusPill status={ticket.status} overdue={ticket.is_overdue} />
          {ticket.reopened_count > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 text-rose-700 border border-rose-100 px-2 py-0.5 text-[11px] font-semibold">
              Reopened ×{ticket.reopened_count}
            </span>
          )}
        </div>
        <h1 className="font-display font-bold text-2xl sm:text-3xl text-slate-900">{ticket.title}</h1>
        <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{ticket.description}</p>

        {(ticket.vendor_id && ticket.vendor_name) || (ticket.asset_id && ticket.asset_name) || canEditMeta ? (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {ticket.vendor_id && ticket.vendor_name ? (
              <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 text-amber-900 px-3 py-1.5 text-xs">
                <HardHat className="w-3.5 h-3.5" />
                <span className="font-semibold">Vendor:</span>
                <span data-testid="ticket-vendor-name">{ticket.vendor_name}</span>
                {canEditMeta && (
                  <button
                    data-testid="change-vendor-btn"
                    onClick={() => { setNewVendorId(ticket.vendor_id); setVendorDialogOpen(true); }}
                    className="ml-1 text-amber-900 hover:text-amber-700"
                    title="Change vendor"
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                )}
              </div>
            ) : canEditMeta ? (
              <button
                data-testid="attach-vendor-btn"
                onClick={() => { setNewVendorId("__none__"); setVendorDialogOpen(true); }}
                className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 border border-dashed border-slate-300 text-slate-600 hover:bg-slate-100 hover:text-slate-800 px-3 py-1.5 text-xs"
              >
                <HardHat className="w-3.5 h-3.5" /> + Attach vendor
              </button>
            ) : null}

            {ticket.asset_id && ticket.asset_name ? (
              <div className="inline-flex items-center gap-2 rounded-full bg-sky-50 border border-sky-200 text-sky-900 px-3 py-1.5 text-xs">
                <Package className="w-3.5 h-3.5" />
                <span className="font-semibold">Asset:</span>
                <span data-testid="ticket-asset-name">{ticket.asset_name}</span>
                {canEditMeta && (
                  <button
                    data-testid="change-asset-btn"
                    onClick={() => { setNewAssetId(ticket.asset_id); setAssetDialogOpen(true); }}
                    className="ml-1 text-sky-900 hover:text-sky-700"
                    title="Change asset"
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                )}
              </div>
            ) : canEditMeta && !isInternalJob ? (
              <button
                data-testid="attach-asset-btn"
                onClick={() => { setNewAssetId("__none__"); setAssetDialogOpen(true); }}
                className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 border border-dashed border-slate-300 text-slate-600 hover:bg-slate-100 hover:text-slate-800 px-3 py-1.5 text-xs"
              >
                <Package className="w-3.5 h-3.5" /> + Attach asset
              </button>
            ) : null}
          </div>
        ) : null}

        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 text-xs">
          <div>
            <div className="text-slate-500">Outlet</div>
            <div className="font-semibold text-slate-800 flex items-center gap-1"><Building2 className="w-3 h-3" />{outlet ? `${outlet.code} · ${outlet.name}` : "—"}</div>
          </div>
          <div>
            <div className="text-slate-500">Raised by</div>
            <div className="font-semibold text-slate-800">{ticket.created_by_name}</div>
          </div>
          <div>
            <div className="text-slate-500">Assigned to</div>
            <div className="font-semibold text-slate-800 flex items-center gap-1">
              <UserCheck className="w-3 h-3" />
              {ticket.assigned_fc_id
                ? (fcs.find(f => f.id === ticket.assigned_fc_id)?.name || "Facility Incharge")
                : "Unassigned"}
            </div>
          </div>
          <div>
            <div className="text-slate-500">Last update</div>
            <div className="font-semibold text-slate-800">{relTime(ticket.updated_at)}</div>
          </div>
        </div>

        {/* Timing panel — precise stamps so the team can see how long the fix actually took */}
        <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          <div>
            <div className="text-slate-500 uppercase tracking-wide text-[10px] font-semibold">Created At</div>
            <div data-testid="ticket-created-at" className="font-semibold text-slate-900 tabular-nums mt-0.5">{formatDateTime(ticket.created_at)}</div>
            {ticket.auto_generated_at && (
              <div className="text-[10px] text-indigo-700 mt-1 inline-flex items-center gap-1">
                <Repeat className="w-3 h-3" /> Auto-generated <span className="font-semibold" data-testid="ticket-auto-generated-at">{formatDateTime(ticket.auto_generated_at)}</span>
              </div>
            )}
          </div>
          <div>
            <div className="text-slate-500 uppercase tracking-wide text-[10px] font-semibold">Resolved At</div>
            <div data-testid="ticket-resolved-at" className={`font-semibold tabular-nums mt-0.5 ${ticket.resolved_at ? "text-emerald-700" : "text-slate-400"}`}>
              {ticket.resolved_at ? formatDateTime(ticket.resolved_at) : "Not yet resolved"}
            </div>
          </div>
          <div>
            <div className="text-slate-500 uppercase tracking-wide text-[10px] font-semibold">Time to Resolve</div>
            <div data-testid="ticket-time-to-resolve" className={`font-semibold tabular-nums mt-0.5 ${ticket.resolved_at ? "text-slate-900" : "text-slate-400"}`}>
              {ticket.resolved_at ? formatDuration(ticket.created_at, ticket.resolved_at) : "—"}
            </div>
          </div>
        </div>
      </div>

      {/* Photos */}
      {(ticket.photo_ids?.length > 0 || ticket.after_photo_ids?.length > 0) && (
        <div className="rounded-2xl bg-white border border-slate-200 p-5">
          {ticket.photo_ids?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <ImageIcon className="w-4 h-4 text-slate-500" />
                <h3 className="font-semibold text-slate-800 text-sm">Original proof</h3>
              </div>
              <div className="flex gap-2 overflow-x-auto snap-row -mx-1 px-1">
                {ticket.photo_ids.map((p) => (
                  <div key={p} className="flex-shrink-0 w-36 h-36 rounded-lg overflow-hidden border border-slate-200">
                    <AuthImage fileId={p} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          )}
          {ticket.after_photo_ids?.length > 0 && (
            <div className="mt-5">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <h3 className="font-semibold text-slate-800 text-sm">After proof</h3>
              </div>
              <div className="flex gap-2 overflow-x-auto snap-row -mx-1 px-1">
                {ticket.after_photo_ids.map((p) => (
                  <div key={p} className="flex-shrink-0 w-36 h-36 rounded-lg overflow-hidden border border-emerald-200">
                    <AuthImage fileId={p} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
              {ticket.resolution_note && (
                <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-100 p-3 text-sm text-emerald-900">
                  <span className="font-semibold">Resolution: </span>{ticket.resolution_note}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Payments */}
      {(user?.role !== "outlet_incharge") && (
        <div className="rounded-2xl bg-white border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-800 text-sm inline-flex items-center gap-2">
              <IndianRupee className="w-4 h-4 text-slate-500" /> Payments
            </h3>
            {canWork && (
              <Dialog open={payOpen} onOpenChange={(v) => { setPayOpen(v); if (!v) setEditingPayment(null); }}>
                <DialogTrigger asChild>
                  <button data-testid="log-payment-btn" onClick={() => { setEditingPayment(null); setPayAmount(""); setPayNote(""); setPayReceipt([]); }} className="text-xs font-semibold text-indigo-600 hover:underline">+ Log payment</button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader><DialogTitle>{editingPayment ? "Edit payment" : "Log a payment"}</DialogTitle></DialogHeader>
                  <form onSubmit={submitPayment} className="space-y-3">
                    <div>
                      <Label>Amount (₹)</Label>
                      <Input data-testid="pay-amount" type="number" step="0.01" min="0" required value={payAmount} onChange={(e) => setPayAmount(e.target.value)} className="mt-1.5 h-11" />
                    </div>
                    <div>
                      <Label>Note</Label>
                      <Textarea data-testid="pay-note" value={payNote} onChange={(e) => setPayNote(e.target.value)} className="mt-1.5" placeholder="e.g. Plumber charges + parts" />
                    </div>
                    <div>
                      <Label>Receipt (optional)</Label>
                      <div className="mt-1.5">
                        <PhotoUploader value={payReceipt} onChange={setPayReceipt} max={1} testid="pay-receipt" />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="submit" disabled={paying} data-testid="pay-submit" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                        {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save payment"}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
          {ticket.payments.length === 0 ? (
            <div className="text-xs text-slate-500">No payments logged for this ticket.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {ticket.payments.map((p) => (
                <li key={p.id} data-testid={`payment-row-${p.id}`} className="py-2.5 flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 text-sm">{currency(p.amount)}</div>
                    <div className="text-[11px] text-slate-500">
                      {p.logged_by_name} · {relTime(p.created_at)}
                      {p.edited_by_name && <span className="italic"> · edited by {p.edited_by_name}</span>}
                    </div>
                    {p.note && <div className="text-xs text-slate-600 mt-0.5">{p.note}</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    {p.receipt_id && (
                      <a href="#" onClick={(e) => e.preventDefault()} className="hidden sm:block w-14 h-14 rounded-lg overflow-hidden border border-slate-200">
                        <AuthImage fileId={p.receipt_id} className="w-full h-full object-cover" />
                      </a>
                    )}
                    {user?.role === "admin" && (
                      <div className="flex items-center gap-1">
                        <button
                          data-testid={`edit-payment-${p.id}`}
                          onClick={() => openEditPayment(p)}
                          className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                          title="Edit payment"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          data-testid={`delete-payment-${p.id}`}
                          onClick={() => removePayment(p)}
                          className="p-1.5 rounded-md text-rose-600 hover:bg-rose-50"
                          title="Delete payment"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              ))}
              <li className="pt-3 mt-2 border-t border-slate-100 flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Total</span>
                <span data-testid="payments-total" className="font-display font-bold text-slate-900">{currency(ticket.payments.reduce((s, p) => s + p.amount, 0))}</span>
              </li>
            </ul>
          )}
        </div>
      )}

      {/* Comments */}
      <div className="rounded-2xl bg-white border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare className="w-4 h-4 text-slate-500" />
          <h3 className="font-semibold text-slate-800 text-sm">Discussion</h3>
        </div>
        <div className="space-y-3">
          {ticket.comments.length === 0 ? (
            <div className="text-xs text-slate-500">No comments yet — start the conversation below.</div>
          ) : ticket.comments.map((c) => (
            <div key={c.id} data-testid={`comment-${c.id}`} className="flex gap-3">
              <div className="h-8 w-8 rounded-full bg-slate-100 grid place-items-center text-xs font-bold text-slate-700 flex-shrink-0">
                {(c.user_name || "?").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="font-semibold text-slate-900 text-sm">{c.user_name}</span>
                  <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">{ROLE_LABEL[c.user_role] || c.user_role}</span>
                  <span className="text-[11px] text-slate-500">· {relTime(c.created_at)}</span>
                </div>
                <div className="text-sm text-slate-700 whitespace-pre-wrap mt-0.5">{c.text}</div>
              </div>
            </div>
          ))}
        </div>
        {!isManager && (
          <form onSubmit={postComment} className="mt-4 flex items-end gap-2">
            <Textarea data-testid="comment-input" value={comment} onChange={(e) => setComment(e.target.value)}
              placeholder="Write a comment…" className="min-h-11 flex-1" />
            <Button type="submit" disabled={posting || !comment.trim()} data-testid="comment-submit" className="h-11 bg-indigo-600 hover:bg-indigo-700 text-white">
              {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </form>
        )}
      </div>

      {/* Action bar */}
      {(canReassign || !isManager) && (
        <div className="fixed bottom-16 md:bottom-4 inset-x-0 z-20 px-4">
          <div className="mx-auto max-w-4xl rounded-2xl bg-white/95 backdrop-blur-xl border border-slate-200 shadow-lg p-2 flex flex-wrap items-center gap-2 justify-end">
            {canReassign && (
              <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="ticket-reassign" variant="outline" className="h-11">
                    <ArrowRightLeft className="w-4 h-4 mr-1.5" /> Reassign
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader><DialogTitle>Transfer ticket</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div className="text-xs text-slate-600">
                      Currently with{" "}
                      <span className="font-semibold text-slate-800">
                        {ticket.assigned_fc_id
                          ? (fcs.find(f => f.id === ticket.assigned_fc_id)?.name || "another Facility Incharge")
                          : "no one"}
                      </span>. Pick who should take it next.
                    </div>
                    <div>
                      <Label>Assign to</Label>
                      <Select value={newFcId} onValueChange={setNewFcId}>
                        <SelectTrigger data-testid="reassign-fc" className="mt-1.5 h-11">
                          <SelectValue placeholder="Choose Facility Incharge" />
                        </SelectTrigger>
                        <SelectContent>
                          {fcs.filter(f => f.id !== ticket.assigned_fc_id).map(f => (
                            <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {fcs.length <= 1 && (
                        <p className="text-[11px] text-amber-700 mt-1">Add another Facility Incharge on Manage → Users first.</p>
                      )}
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      onClick={reassign}
                      disabled={reassigning || !newFcId}
                      data-testid="reassign-submit"
                      className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    >
                      {reassigning ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm transfer"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
            {!isManager && user?.role === "admin" && (
              <Button variant="ghost" onClick={deleteTicket} data-testid="ticket-delete" className="text-rose-600 hover:bg-rose-50 hover:text-rose-700">
                <Trash2 className="w-4 h-4 mr-1.5" /> Delete
              </Button>
            )}
            {!isManager && ticket.status === "resolved" && (
              <Button variant="outline" onClick={reopen} data-testid="ticket-reopen" className="h-11">
                <RotateCcw className="w-4 h-4 mr-1.5" /> Reopen
              </Button>
            )}
            {!isManager && canWork && ticket.status === "open" && (
              <Button onClick={startWork} data-testid="ticket-start" variant="outline" className="h-11">
                <Play className="w-4 h-4 mr-1.5" /> Start work
              </Button>
            )}
            {canWork && ticket.status !== "resolved" && (
              <Dialog open={resolveOpen} onOpenChange={setResolveOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="ticket-resolve" className="h-11 bg-emerald-600 hover:bg-emerald-700 text-white">
                    <CheckCircle2 className="w-4 h-4 mr-1.5" /> Resolve
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader><DialogTitle>Mark as resolved</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    {!isInternalJob && (
                      <div>
                        <Label className="flex items-center gap-1.5">
                          <Camera className="w-4 h-4 text-slate-500" /> After photo <span className="text-rose-600">*</span>
                        </Label>
                        <div className="mt-1.5">
                          <PhotoUploader value={afterPhotos} onChange={setAfterPhotos} max={4} testid="after-photos" />
                        </div>
                      </div>
                    )}
                    <div>
                      <Label>Resolution note</Label>
                      <Textarea data-testid="resolution-note" value={resNote} onChange={(e) => setResNote(e.target.value)} placeholder="What did you do to fix it?" className="mt-1.5 min-h-24" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      onClick={resolveNow}
                      disabled={resolving || (!isInternalJob && afterPhotos.length === 0)}
                      data-testid="confirm-resolve"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      {resolving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm resolve"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      )}
      {/* Vendor / Asset attach-later dialogs */}
      <Dialog open={vendorDialogOpen} onOpenChange={setVendorDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{ticket.vendor_id ? "Change vendor" : "Attach vendor"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>Vendor</Label>
            <Select value={newVendorId || "__none__"} onValueChange={setNewVendorId}>
              <SelectTrigger data-testid="vendor-picker" className="h-11"><SelectValue placeholder="Pick a vendor" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No vendor</SelectItem>
                {vendors.map(v => (
                  <SelectItem key={v.id} value={v.id}>{v.name}{v.trade ? ` · ${v.trade}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {vendors.length === 0 && (
              <p className="text-[11px] text-slate-500">No vendors yet — add one from Manage → Vendors.</p>
            )}
          </div>
          <DialogFooter>
            <Button onClick={saveVendor} disabled={savingVendor} data-testid="vendor-picker-save" className="bg-indigo-600 hover:bg-indigo-700 text-white">
              {savingVendor ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={assetDialogOpen} onOpenChange={setAssetDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{ticket.asset_id ? "Change asset" : "Attach asset"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>Asset at this outlet</Label>
            <Select value={newAssetId || "__none__"} onValueChange={setNewAssetId}>
              <SelectTrigger data-testid="asset-picker" className="h-11"><SelectValue placeholder="Pick asset" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No specific asset</SelectItem>
                {outletAssets.map(a => (
                  <SelectItem key={a.id} value={a.id}>{a.name}{a.category ? ` · ${a.category}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {outletAssets.length === 0 && (
              <p className="text-[11px] text-slate-500">No assets registered for this outlet — add one from Manage → Assets.</p>
            )}
          </div>
          <DialogFooter>
            <Button onClick={saveAsset} disabled={savingAsset} data-testid="asset-picker-save" className="bg-indigo-600 hover:bg-indigo-700 text-white">
              {savingAsset ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
